import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiEdit2, FiShield, FiSave, FiX, FiInfo, FiKey, FiMail } = FiIcons;

export default function ProfileManagement({ currentUser, onProfileUpdated }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({ role: 'viewer' });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error al cargar perfiles:', error);
      setError('No se pudieron cargar los perfiles. Verifica tus permisos.');
    } else {
      setProfiles(data || []);
    }
    setLoading(false);
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({ role: profile.role });
    setError(null);
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!editingProfile) return;

    // Utilizamos la función RPC (Remote Procedure Call) que creamos en la base de datos.
    // Como restringimos el UPDATE en 'profiles' para que los usuarios solo modifiquen su propia fila,
    // es necesario usar esta función segura para que un admin edite el rol de alguien más.
    const { error: updateError } = await supabase
      .rpc('admin_update_user_role', {
        target_user_id: editingProfile.id,
        new_role: formData.role
      });

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setProfiles(prev => prev.map(p => 
      p.id === editingProfile.id ? { ...p, role: formData.role } : p
    ));
    
    setView('table');

    if (onProfileUpdated) {
      await onProfileUpdated();
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {view === 'table' ? 'Gestión de Perfiles' : 'Editar Rol de Usuario'}
          </h2>
          <p className="text-gray-500 mt-1">
            Administra los roles y niveles de acceso de los usuarios registrados.
          </p>
        </div>
      </div>

      <div className="mb-6 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-xl p-4 flex items-start">
        <SafeIcon icon={FiInfo} className="text-indigo-500 mt-0.5 mr-3 text-lg flex-shrink-0" />
        <div className="text-sm">
          <strong>Seguridad Activa:</strong> Esta vista lista a todos los usuarios del sistema. Gracias a las políticas de seguridad (RLS), <strong>solo tú (Administrador)</strong> puedes ver esta tabla y modificar roles. Los intentos de otros usuarios serán bloqueados directamente por la base de datos.
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      ) : view === 'table' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {error && (
            <div className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                  <th className="px-6 py-4">Usuario</th>
                  <th className="px-6 py-4">Rol Asignado</th>
                  <th className="px-6 py-4">Fecha de Registro</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {profiles.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No se encontraron perfiles.
                    </td>
                  </tr>
                ) : (
                  profiles.map((p) => {
                    const isMe = currentUser && p.id === currentUser.id;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold mr-3 ${
                              isMe ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {p.email ? p.email.charAt(0).toUpperCase() : <SafeIcon icon={FiUsers} />}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 flex items-center">
                                {p.email || 'Usuario sin correo'}
                                {isMe && <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Tú</span>}
                              </div>
                              <div className="text-xs text-gray-500 font-mono mt-0.5 flex items-center">
                                <SafeIcon icon={FiKey} className="mr-1" />
                                {p.id.split('-')[0]}...{p.id.split('-')[4]}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                            p.role === 'admin' 
                              ? 'bg-indigo-100 text-indigo-800' 
                              : p.role === 'editor'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            <SafeIcon icon={p.role === 'admin' ? FiShield : FiUsers} className="mr-1.5" />
                            {p.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600 text-sm">
                          {new Date(p.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleEdit(p)}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors inline-flex items-center"
                            title="Editar Rol"
                          >
                            <SafeIcon icon={FiEdit2} className="mr-1" />
                            <span className="text-sm font-medium hidden sm:inline">Editar</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-2xl mx-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
              {error}
            </div>
          )}

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center">
            <div className="h-12 w-12 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xl mr-4">
              {editingProfile?.email ? editingProfile.email.charAt(0).toUpperCase() : <SafeIcon icon={FiUsers} />}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Editando rol para:</p>
              <p className="font-medium text-gray-900">{editingProfile?.email || 'Usuario sin correo'}</p>
              <p className="text-xs text-gray-400 font-mono mt-0.5">ID: {editingProfile?.id}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol de Acceso</label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiShield} className="text-gray-400" />
                </div>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors bg-white"
                >
                  <option value="viewer">Viewer (Solo lectura)</option>
                  <option value="editor">Editor (Puede crear y editar inventario)</option>
                  <option value="admin">Administrador (Acceso total)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setView('table')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
              >
                <SafeIcon icon={FiX} className="mr-2" />
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center shadow-sm"
              >
                <SafeIcon icon={FiSave} className="mr-2" />
                Actualizar Rol
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}